import React, { useEffect, useMemo, useState } from "react";
import * as contactService from "../../services/contactService";

const styles = {
  page: {
    padding: "28px 36px",
    background: "#fbf7fa",
    minHeight: "100%",
  },
  heroTitle: {
    fontSize: "24px",
    fontWeight: 800,
    color: "#8f1632",
    margin: 0,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  },
  heroSubtitle: {
    color: "#ff4f9a",
    marginTop: 8,
    marginBottom: 28,
    fontWeight: 600,
  },
  card: {
    background: "#fff",
    border: "1px solid #f1d5df",
    borderRadius: 18,
    padding: 22,
    boxShadow: "0 8px 20px rgba(143, 22, 50, 0.04)",
  },
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: 800,
    margin: 0,
    color: "#111827",
  },
  desc: {
    color: "#4b5563",
    marginTop: 6,
    lineHeight: 1.6,
  },
  statsRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 18,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 12px",
    borderRadius: 999,
    background: "#fde7ef",
    color: "#8f1632",
    fontWeight: 700,
    fontSize: 13,
  },
  search: {
    width: "100%",
    border: "1px solid #ead5dd",
    borderRadius: 12,
    padding: "11px 14px",
    background: "#fff",
    marginBottom: 16,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  item: {
    border: "1px solid #f2e3e8",
    borderRadius: 16,
    padding: 16,
    background: "#fffdfd",
  },
  itemTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  name: {
    margin: 0,
    fontWeight: 800,
    color: "#111827",
  },
  meta: {
    color: "#6b7280",
    fontSize: 13,
    lineHeight: 1.6,
    marginTop: 6,
  },
  messageBody: {
    marginTop: 12,
    color: "#374151",
    lineHeight: 1.7,
    whiteSpace: "pre-wrap",
  },
  actionRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginTop: 14,
  },
  btn: {
    border: "1px solid #f3c7d7",
    background: "#fff",
    color: "#8f1632",
    borderRadius: 12,
    padding: "9px 12px",
    fontWeight: 700,
    cursor: "pointer",
  },
  empty: {
    padding: 18,
    border: "1px dashed #f1c2d3",
    borderRadius: 14,
    color: "#6b7280",
    background: "#fff",
  },
};

function asArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isMessageRead(item) {
  const status = String(item?.status || "").toLowerCase();
  return (
    item?.is_read === true ||
    status === "read" ||
    status === "resolved" ||
    status === "done"
  );
}

export default function MessagesPage() {
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        const payload = await contactService.getAdminMessages();
        if (!active) return;
        setMessages(asArray(payload));
      } catch (error) {
        if (!active) return;
        setMessages([]);
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return messages;

    return messages.filter((item) => {
      const hay = [
        item?.name,
        item?.sender_name,
        item?.email,
        item?.phone,
        item?.subject,
        item?.message,
        item?.content,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(keyword);
    });
  }, [messages, search]);

  const unreadCount = useMemo(() => {
    return messages.filter((item) => !isMessageRead(item)).length;
  }, [messages]);

  async function handleMarkRead(item) {
    try {
      const nextStatus = "read";
      let result = null;

      if (typeof contactService.updateMessageStatus === "function") {
        result = await contactService.updateMessageStatus(item.id, nextStatus);
      } else if (typeof contactService.updateContactMessageStatus === "function") {
        result = await contactService.updateContactMessageStatus(item.id, nextStatus);
      } else {
        alert("Fitur update status pesan belum tersedia di contactService.");
        return;
      }

      const updated = result?.data || result || {
        ...item,
        status: nextStatus,
        is_read: true,
      };

      setMessages((prev) =>
        prev.map((row) =>
          row.id === item.id
            ? {
                ...row,
                ...updated,
                status: updated?.status || nextStatus,
                is_read: updated?.is_read ?? true,
              }
            : row
        )
      );
    } catch (error) {
      alert(error?.message || "Gagal mengubah status pesan.");
    }
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.heroTitle}>Dashboard Solusi IoT Cerdas</h1>
      <div style={styles.heroSubtitle}>
        Kelola data dan pengaturan untuk website Solusi IoT Cerdas.
      </div>

      <div style={styles.card}>
        <div style={styles.topBar}>
          <div>
            <h2 style={styles.title}>Pesan Masuk</h2>
            <div style={styles.desc}>
              Pesan dari form kontak website sekarang dibaca dari backend contacts API.
            </div>
          </div>
        </div>

        <div style={styles.statsRow}>
          <span style={styles.badge}>Total Pesan: {messages.length}</span>
          <span style={styles.badge}>Belum Dibaca: {unreadCount}</span>
        </div>

        <input
          style={styles.search}
          placeholder="Cari nama, email, nomor, atau isi pesan..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {loading ? <div style={styles.empty}>Memuat pesan...</div> : null}

        {!loading && !filtered.length ? (
          <div style={styles.empty}>
            Belum ada pesan. Pesan dari form kontak website akan muncul di halaman ini.
          </div>
        ) : null}

        {!loading && filtered.length ? (
          <div style={styles.list}>
            {filtered.map((item, index) => {
              const read = isMessageRead(item);

              return (
                <div key={item?.id || index} style={styles.item}>
                  <div style={styles.itemTop}>
                    <div>
                      <p style={styles.name}>
                        {item?.name || item?.sender_name || "Tanpa Nama"}
                      </p>
                      <div style={styles.meta}>
                        {item?.email || "-"} <br />
                        {item?.phone || item?.whatsapp || "-"} <br />
                        {formatDate(item?.created_at || item?.updated_at)}
                      </div>
                    </div>

                    <span
                      style={{
                        ...styles.badge,
                        background: read ? "#eef2ff" : "#fde7ef",
                        color: read ? "#3730a3" : "#8f1632",
                      }}
                    >
                      {read ? "Sudah Dibaca" : "Belum Dibaca"}
                    </span>
                  </div>

                  {item?.subject ? (
                    <div style={{ marginTop: 12, fontWeight: 700 }}>
                      Subject: {item.subject}
                    </div>
                  ) : null}

                  <div style={styles.messageBody}>
                    {item?.message || item?.content || "Tidak ada isi pesan."}
                  </div>

                  {!read ? (
                    <div style={styles.actionRow}>
                      <button
                        type="button"
                        style={styles.btn}
                        onClick={() => handleMarkRead(item)}
                      >
                        Tandai Sudah Dibaca
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}